import { useEffect } from 'react'

/**
 * Wire a `beforeunload` browser-level prompt to a boolean predicate. While
 * the predicate returns `true`, the user gets the browser's native
 * "are you sure you want to leave?" dialog on tab close / reload / OS-level
 * navigation.
 *
 * In-app react-router navigation guards are out of scope for this hook —
 * callers compose them via `react-router`'s `useBlocker` separately so this
 * stays a thin, framework-agnostic primitive.
 *
 * @param isDirty Predicate. When `true`, the guard is armed. Callers may
 *                pass either a stable function (memoised) or a fresh
 *                function each render — the hook only depends on the
 *                returned boolean, not the reference.
 */
export function useUnsavedChangesGuard(isDirty: () => boolean): void {
  // We re-evaluate `isDirty()` synchronously inside the effect so changes
  // to staged state during the same render are reflected next paint.
  const dirty = isDirty()
  useEffect(() => {
    if (!dirty) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Most browsers ignore the string but still show their native prompt
      // whenever `returnValue` is assigned.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])
}
